import groupBy from 'lodash/groupBy';
import {
  PortfolioAllocation,
  PortfolioAllocationDataPoint,
  PortfolioSectorAllocationTable,
} from '@models';
import { sortPortfolioDataPoints } from '@products/portfolio/portfolio.utils';
import {
  PortfolioSectorAllocationDTO,
  PortfolioDTO,
  PortfolioComponentId,
  ConfigurationCountry,
  ConfigurationLocale,
  FundId,
} from '@types';
import { formatPercentage } from '@utils/text/number-utils';
import { MapperFactory } from '../mapper-factory';
import {
  getFirstNonEmptyProp,
  hasValue,
  parseStdNumber,
  parseYN,
} from '../mapper.utils';
import { Mapper, MapperParams } from '../type.mapper';
import { Logger } from '@utils/logger';

const logger = Logger.getLogger('SectorAllocationMapper');

export const createAllocationFilter = (allocationHeadingStd: string) => (
  point: PortfolioSectorAllocationDTO
) => point.allocheadingstd === allocationHeadingStd;

/**
 * example fund:
 * https://en-us.staging.ftsites.com/investments/options/mutual-funds/products/4402/A/franklin-biotechnology-discovery-fund/FBDIX#portfolio
 */
export const mapSectorAllocation = (
  data: PortfolioSectorAllocationDTO[],
  componentId: PortfolioComponentId,
  baseLabelKey: string
): PortfolioAllocation => ({
  componentId,
  caveatComponentId: PortfolioComponentId.SECTOR_ALLOCATION,
  baseLabelKey,
  allocationFlag: getFirstNonEmptyProp(data, 'allocflag'),
  allocationHeading: getFirstNonEmptyProp(data, 'allocheadinglocal'),
  asOfDate: getFirstNonEmptyProp(data, 'asofdate'),
  calculationBasis: getFirstNonEmptyProp(data, 'calcbasislocal'),
  calculationType: getFirstNonEmptyProp(data, 'calcname'),
  hasDerivatives: parseYN(getFirstNonEmptyProp(data, 'hasderivatives')),
  updateFrequency: getFirstNonEmptyProp(data, 'frequency'),
  data: data.sort(sortPortfolioDataPoints).map(
    (obj: PortfolioSectorAllocationDTO): PortfolioAllocationDataPoint => ({
      label: obj.sectorname,
      breakdown: formatPercentage(obj.brkdwnpct),
      breakdownStd: parseStdNumber(obj.brkdwnpctstd),
      benchmark: formatPercentage(obj.bmbrkdwnpct),
      benchmarkStd: parseStdNumber(obj.bmbrkdwnpctstd),
    })
  ),
});

/**
 * Static config class for the different types of Sector Allocations
 * config class based on this https://stackoverflow.com/a/51398471
 */
export class SectorAllocationConfig {
  // private to disallow creating other instances of this type
  private constructor(
    public readonly componentId: PortfolioComponentId,
    // used for labels, caveats etc e.g. "top-ten-holding-money-funds"
    public readonly baseLabelKey: string,
    // used to convert filtered data into a PortfolioAllocation object
    public readonly mapFn: (
      data: PortfolioSectorAllocationDTO[],
      componentId: PortfolioComponentId,
      baseLabelKey: string,
      mapperParams?: MapperParams
    ) => PortfolioAllocation,
    // used to filter data to a specific sector allocation type
    public readonly filterFn?: (point: PortfolioSectorAllocationDTO) => boolean
  ) {}
  // configs

  // tslint:disable-next-line:max-line-length
  // https://en-us.staging.ftsites.com/investments/options/mutual-funds/products/4402/A/franklin-biotechnology-discovery-fund/FBDIX#portfolio
  static readonly SECTOR_ALLOCATION = new SectorAllocationConfig(
    PortfolioComponentId.SECTOR_ALLOCATION,
    'sector-allocation',
    mapSectorAllocation
    // no filterFn, as this is the default config to catch any rows that don't pass the other filters
  );

  static allConfigs: SectorAllocationConfig[] = [
    SectorAllocationConfig.SECTOR_ALLOCATION,
  ];

  // returns matching config or undefined
  static getConfig(componentId: PortfolioComponentId): SectorAllocationConfig {
    return this.allConfigs.find(
      (config: SectorAllocationConfig): boolean =>
        config.componentId === componentId
    ); // just match on key
  }

  // e.g. console.log(SectorAllocationConfig.SECTOR_ALLOCATION) prints 'SECTOR_ALLOCATION'
  toString() {
    return this.componentId;
  }
}

export interface AllocationBucket {
  config: SectorAllocationConfig;
  data: PortfolioSectorAllocationDTO[];
}

export const reduceSectorData = (
  buckets: { [id: string]: AllocationBucket },
  point: PortfolioSectorAllocationDTO
): { [id: string]: AllocationBucket } => {
  // 1. see which allocation type filter passes for this row
  let config: SectorAllocationConfig = SectorAllocationConfig.allConfigs.find(
    (c: SectorAllocationConfig): boolean => c.filterFn && c.filterFn(point)
  );
  if (!config) {
    // set to default config if it doesn't pass any filters
    config = SectorAllocationConfig.SECTOR_ALLOCATION;
  }

  // 2. create new bucket if it doesn't already exist
  if (!buckets[config.componentId]) {
    buckets[config.componentId] = { config, data: [] };
  }

  // 3. add row to bucket
  buckets[config.componentId].data.push(point);

  return buckets;
};

export const mapSectorBuckets = (
  bucket: AllocationBucket
): PortfolioAllocation =>
  bucket.config.mapFn(
    bucket.data,
    bucket.config.componentId,
    bucket.config.baseLabelKey
  );

export class SectorAllocationMapper extends Mapper<PortfolioAllocation[]> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(portfolioDto: PortfolioDTO): PortfolioAllocation[] {
    const data: PortfolioSectorAllocationDTO[] = portfolioDto.sectorallocation;

    // if no data, return null
    if (!data || data.length === 0) {
      return null;
    }
    // 1. split data into buckets depending on Sector Allocation type
    // sometimes the data can contain rows for multiple sector charts
    // e.g. Sector Equity + Sector Fixed Income charts both displayed for one fund
    // Most of the time, there will just be one Sector Allocation chart
    const buckets: {
      [id: string]: AllocationBucket;
    } = data.reduce(reduceSectorData, {});
    logger.debug('sector buckets', JSON.stringify(buckets, null, 2));

    // 2. map buckets into allocation objects
    const allocations: PortfolioAllocation[] = Object.values(buckets).map(
      mapSectorBuckets
    );
    logger.debug('sector allocations', allocations);

    return allocations;
  }
}

export class SectorAllocationsTableMapper extends Mapper<
  PortfolioSectorAllocationTable[]
> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(portfolioDto: PortfolioDTO): PortfolioSectorAllocationTable[] {
    const portfolioSectorAllocationsDTO: PortfolioSectorAllocationDTO[] =
      portfolioDto.sectorallocation;
    const portfolioSectorAllocationsTable: PortfolioSectorAllocationTable[] = [];

    if (Array.isArray(portfolioSectorAllocationsDTO)) {
      portfolioSectorAllocationsDTO
        .sort(sortPortfolioDataPoints)
        .forEach((portfolioSectorAllocationDTO) => {
          const portfolioSectorAllocationTable: PortfolioSectorAllocationTable = this.createMapper(
            SectorAllocationTableMapper
          ).toDomain(portfolioSectorAllocationDTO);

          if (portfolioSectorAllocationTable) {
            portfolioSectorAllocationsTable.push(
              portfolioSectorAllocationTable
            );
          }
        });
    }

    return portfolioSectorAllocationsTable;
  }
}

export class SectorAllocationTableMapper extends Mapper<PortfolioSectorAllocationTable> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(
    portfolioSectorAllocationDTO: PortfolioSectorAllocationDTO
  ): PortfolioSectorAllocationTable {
    return {
      fundId: portfolioSectorAllocationDTO.fundid as FundId,
      countryCode: portfolioSectorAllocationDTO.cntrycode as ConfigurationCountry,
      languageCode: portfolioSectorAllocationDTO.langcode as ConfigurationLocale,
      asOfDate: portfolioSectorAllocationDTO.asofdate,
      asOfDateStd: portfolioSectorAllocationDTO.asofdatestd,
      updateFrequency: portfolioSectorAllocationDTO.frequency,
      sectorName: portfolioSectorAllocationDTO.sectorname,
      breakdownPercentage: portfolioSectorAllocationDTO.brkdwnpct,
      calculationBasis: portfolioSectorAllocationDTO.calcbasislocal,
      calculationType: portfolioSectorAllocationDTO.calcname,
      allocationFlag: portfolioSectorAllocationDTO.allocflag,
      progressBarColor: portfolioSectorAllocationDTO.progressBarColor,
      hasDerivatives: parseYN(portfolioSectorAllocationDTO.hasderivatives),
      breakdown: formatPercentage(portfolioSectorAllocationDTO.brkdwnpct),
      breakdownStd: parseStdNumber(portfolioSectorAllocationDTO.brkdwnpctstd),
      benchmark: formatPercentage(portfolioSectorAllocationDTO.bmbrkdwnpct),
      benchmarkStd: parseStdNumber(portfolioSectorAllocationDTO.bmbrkdwnpctstd),
    };
  }
}
